// Times, spans and money as the language shown says them: dates through
// Intl in that language and the viewer's zone, spans from format.json.
import { language, t } from "./core";

/** "4 min", "2 h", "3 d": a span of whole minutes, as `ago` and `left` both say it. */
function span(m: number): string {
  if (m < 60) return t("format:spanMinutes", { n: m });
  const h = Math.round(m / 60);
  if (h < 48) return t("format:spanHours", { n: h });
  return t("format:spanDays", { n: Math.round(h / 24) });
}

/** "just now", "4 min ago", "2 h ago", "3 d ago": how long before now. */
export function ago(iso: string, now: number = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return iso;
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return t("format:justNow");
  return t("format:ago", { span: span(Math.round(s / 60)) });
}

/** "4 min left", "23 h left", "3 d left": how long until then, as `ago` says how long since. */
export function left(iso: string, now: number = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return iso;
  return t("format:left", { span: span(Math.max(1, Math.round((at - now) / 60_000))) });
}

/** "$0.42" — a cost, two decimals below a dollar shown to the cent. */
export function money(usd: number): string {
  return `$${usd.toFixed(usd < 10 ? 2 : 0)}`;
}

/** A timestamp in the language shown and the viewer's zone. */
export function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(language());
}

/** `when` to the minute: a date and time a person reads, not a log's seconds. */
export function whenMinute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(language(), { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * A day and a time to the minute, this year's without the year: "Oct 30,
 * 4:02 PM", "10월 30일 오후 4:02" — a moment a person keeps to, such as an
 * exam's start and end, said the same whatever day it is read on.
 */
export function whenAt(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(language(), {
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The day alone, this year's without the year: when something was asked, not to the minute. */
export function whenDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(language(), { month: "short", day: "numeric" });
  return d.toLocaleDateString(language(), { year: "numeric", month: "short", day: "numeric" });
}

/** A date alone, in full: a day a person is told something lasts until. */
export function dateOnly(d: Date): string {
  return d.toLocaleDateString(language());
}

/** A day as a date input holds it, YYYY-MM-DD, where the viewer is. */
export function dayInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** `when` for a list's narrow column: the time today, the day this year, else the date. */
export function whenShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(language(), { hour: "numeric", minute: "2-digit" });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(language(), { month: "short", day: "numeric" });
  return d.toLocaleDateString(language(), { year: "numeric", month: "short", day: "numeric" });
}

/**
 * A due date short enough for a chip: "today 12:16", "tomorrow 12:16" — the
 * calendar day, as a person counts it — else the day alone ("Oct 28"), with
 * its year when not this one.
 */
export function dueShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(language(), { hour: "numeric", minute: "2-digit" });
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (d.toDateString() === now.toDateString()) return t("classes:dueTodayAt", { time });
  if (d.toDateString() === tomorrow.toDateString()) return t("classes:dueTomorrowAt", { time });
  return whenDay(iso, now);
}
